import { Formik } from "formik";
import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import * as Yup from "yup";
import { IWizardExecuteAllModel, IWizardStep, Wizard, WizardTypes } from "../../components/Wizard";
import { WizardWrapper } from "../../components/WizardWrapper";
import { AdvancedEmailWizardContext } from "../../context/AdvancedEmailWizardContext";
import { AttachmentContextProvider } from "../../context/AttachmentContext";
import { DynamicTextContextEnum } from "../../enums/DynamicTextContext";
import { WizardTypeEnum } from "../../enums/WizardTypeEnum";
import { strings } from "../../localisation";
import ErrorBoundary from "../ErrorBoundary/ErrorBoundary";
import { Details } from "../Steps/Details/Details";
import { EmailContent } from "../Steps/EmailContent/EmailContent";
import { Preview } from "../Steps/Preview/Preview";
import { IParticipantEmailDataModel } from "./models/ParticipantEmail";
import { ISendEmailDataModel } from "./models/SendEmail";
import { SendEmailExecuteModel } from "./models/SendEmail/SendEmailExecuteModel";
import { ParticipantSelectType } from "./steps/ParticipantDetails/ParticipantSelector";
import { TemplateSelection } from "./steps/TemplateSelection/TemplateSelection";

export interface IEmailWizardProps {
    EntryIDs: number[];
    OnWizardClose: () => void;
    Events: IAdvancedEmailAppEvents;
}

export interface ISendEmailErrorHandler {
    ErrorMessages: string[];
    Resolve: () => void;
}

export const EmailWizard: React.FunctionComponent<IEmailWizardProps> = ({ EntryIDs, OnWizardClose, Events }: IEmailWizardProps) => {

    // Refs
    const formikRef = React.useRef<Formik<IWizardExecuteAllModel>>(null);

    // Context
    const { WizardType, DynamicTextContext, IsLoading } = React.useContext(AdvancedEmailWizardContext);

    // State
    const [temporaryAttachmentFolder] = React.useState(`_attachments_${uuidv4()}`);

    const [wizardHeader] = React.useState(() => {
        switch (DynamicTextContext) {
            case DynamicTextContextEnum.ConcernEntry:
                return strings.Message_Wizard_SendToConcernHeader;
            case DynamicTextContextEnum.IncidentEntry:
                return strings.Message_Wizard_SendToConductHeader;
            case DynamicTextContextEnum.ContributionEntry:
                return strings.Message_Wizard_SendToContributionInteractionHeader;

            default:
                return strings.formatString(
                    EntryIDs.length > 1
                        ? strings.Message_Wizard_SendToHeaderPlural
                        : strings.Message_Wizard_SendToHeader,
                    EntryIDs.length.toLocaleString(),
                ) as string;
        }
    });

    const executeData: ISendEmailDataModel | IParticipantEmailDataModel = {
        TemplateID: -1,

        Attachments: [],
        AttachmentsCollection: [],

        CorrespondenceName: "",
        EmailTo: [],
        EmailToItems: [],

        EmailFromID: -1,
        EmailFromItem: null,

        EmailCC: false,
        EmailCCAddress: [],
        EmailCCAddressItems: [],
        EmailBCC: false,
        EmailBCCAddress: [],
        EmailBCCAddressItems: [],
        CCAddressOnlyFirst: true,
        BCCAddressOnlyFirst: true,
        EmailSubject: "",
        EmailBody: "",
        ViewOnWeb: true,

        UnlayerTemplate: undefined,

        CopyToCorrespondence: false,
        TemporaryAttachmentFolder: temporaryAttachmentFolder,

        transformData() { return; },

    };

    const srExecuteWizardModel: SendEmailExecuteModel<ISendEmailDataModel | IParticipantEmailDataModel> = new SendEmailExecuteModel(EntryIDs, executeData);

    const steps: IWizardStep[] = [
        {
            TrackerProps: {
                ClientId: "step1-tab",
                AriaControlsPanel: "step1-panel",
                Title: "Template",
                AriaLabel: "Template",
            },
            TrackerPanel:
                <TemplateSelection
                    FormikValues={srExecuteWizardModel}
                    ValidationSchema={Yup.object()}
                    AppEvents={Events}
                />,
            HideButtonBar: true,
        },
        {
            NextLabel: strings.getString(`${WizardTypes.Email}_Action_Wizard_Next_Content`),
            PreviousLabel: strings.Email_Action_Wizard_Prev_TemplateSelection,
            TrackerProps: {
                ClientId: "step2-tab",
                AriaControlsPanel: "step2-panel",
                Title: strings.Label_Step_EmailDetails_Title,
                AriaLabel: strings.Label_Step_EmailDetails_AriaLabel,
            },
            TrackerPanel:
                <Details
                    ShowParticipants={WizardType === WizardTypeEnum.Participant}
                    FormikValues={srExecuteWizardModel}
                    ValidationSchema={Yup.object().shape({
                        formDataModel: Yup.object({
                            CorrespondenceName: Yup.string().trim().required().label(strings.Label_Email_Name),
                            EmailSubject: Yup.string().trim().required().label(strings.Label_Email_Subject),
                            EmailToItems: Yup.array().required().nullable().label(strings.Label_Email_RecipientAddress),
                            EmailFromItem: Yup.object().required().nullable().label(strings.Label_Email_FromAddress),
                            CCAddressOnlyFirst: Yup.boolean(),
                            BCCAddressOnlyFirst: Yup.boolean(),
                            EmailCC: Yup.boolean(),
                            EmailCCAddressItems: Yup.array().when(nameof<SendEmailExecuteModel<ISendEmailDataModel>>((m) => m.formDataModel.EmailCC), {
                                is: (value) => value === true, then: () => {
                                    return Yup.array().required().nullable().label(strings.Label_Email_CC);
                                },
                            }),
                            EmailBCC: Yup.boolean(),
                            EmailBCCAddressItems: Yup.array().when(nameof<SendEmailExecuteModel<ISendEmailDataModel>>((m) => m.formDataModel.EmailBCC), {
                                is: (value) => value === true, then: () => {
                                    return Yup.array().required().nullable().label(strings.Label_Email_BCC);
                                },
                            }),
                            ViewOnWeb: Yup.boolean(),

                            ParticipantSelectorOptions: WizardType === WizardTypeEnum.Participant ? Yup.string().required().label(strings.Label_ParticipantSelector_RecipientType) : Yup.string(),
                            ParticipantSelectItems: Yup.array().nullable().when(nameof<SendEmailExecuteModel<IParticipantEmailDataModel>>((m) => m.formDataModel.ParticipantSelectorOptions), {
                                is: (value) => value === ParticipantSelectType.SelectedParticipants, then: () => {
                                    return Yup.array().required(strings.Label_ParticipantSelector_SelectParticipantsInvalid).nullable();
                                },
                            }),
                        }),
                    })}
                    ShouldValidate={true}
                />,
        },
        {
            NextLabel: strings.getString(`${WizardTypes.Email}_Action_Wizard_Next_Preview`),
            PreviousLabel: strings.getString(`${WizardTypes.Email}_Action_Wizard_Prev_Email`),
            TrackerProps: {
                ClientId: "step3-tab",
                AriaControlsPanel: "step3-panel",
                Title: strings.Label_Step_EmailContent_Title,
                AriaLabel: strings.Label_Step_EmailContent_AriaLabel,
            },
            TrackerPanel:
                <EmailContent
                    FormikValues={srExecuteWizardModel}
                    ValidationSchema={Yup.object().shape({
                        formDataModel: Yup.object({
                            EmailBody: Yup.string(), // TODO: this can be strongly typed if we break up the ISendEmailExecuteModel in to models per step
                        }),
                    })}
                    ShouldValidate={true}
                />,
        },
        {
            PreviousLabel: strings.getString(`${WizardTypes.Email}_Action_Wizard_Prev_Content`),
            TrackerProps: {
                ClientId: "step4-tab",
                AriaControlsPanel: "step4-panel",
                Title: strings.Label_Step_EmailPreview_Title,
                AriaLabel: strings.Label_Step_EmailPreview_AriaLabel,
            },
            TrackerPanel:
                <Preview
                    FormikValues={srExecuteWizardModel}
                    ValidationSchema={Yup.object()}
                />,
        },
    ];

    const sendEmailOnComplete = async (model: IWizardExecuteAllModel): Promise<void> => {
        const sendModel = model as SendEmailExecuteModel<ISendEmailDataModel | IParticipantEmailDataModel>;
        sendModel.formDataModel.transformData();

        return new Promise<void>((resolve, reject) => {
            Events?.sendEmail(sendModel.ids!, JSON.stringify(sendModel.formDataModel, sendModel.jsonReplacer))
                .then(() => {
                    resolve();
                }, () => {
                    reject();
                });
        });
    };

    const handleOnClose = (shouldClose: boolean) => {
        if (shouldClose) {
            OnWizardClose();
        }
    };

    return (
        <AttachmentContextProvider
            AttachmentFolder={temporaryAttachmentFolder}
            AttachmentEvents={Events}
            InitialAttachments={srExecuteWizardModel.formDataModel.Attachments}
        >
            <WizardWrapper
                Header={wizardHeader}
                OnCloseConfirm={OnWizardClose}
            >
                <ErrorBoundary>
                    <Wizard
                        IsLoading={IsLoading}
                        Steps={steps}
                        OnComplete={sendEmailOnComplete}
                        OnCloseRequest={handleOnClose}
                        WizardType={WizardTypes.Email}
                        FormikRef={formikRef}
                    />
                </ErrorBoundary>
            </WizardWrapper>
        </AttachmentContextProvider>
    );
};
